import { type OpusEncoder, createOpusEncoder, isOpusSupported } from './opusEncoder';
import type { CaptureMessage, CaptureMode } from './pcmCapture.worklet';
import captureWorkletUrl from './pcmCapture.worklet.ts?worker&url';

type MediaStreamTrackProcessorConstructor = new (init: { track: MediaStreamTrack }) => {
	readable: ReadableStream<AudioData>;
};

declare global {
	interface Window {
		MediaStreamTrackProcessor?: MediaStreamTrackProcessorConstructor;
	}
}

export type AudioFrameSink = (frame: ArrayBuffer) => void;

export type AudioSource = {
	/** Query parameters that tell AssemblyAI how the frames are encoded. */
	encodingParams: Record<string, string>;
	/** Frames are only produced while a sink is set; otherwise audio is dropped. */
	setSink: (sink: AudioFrameSink | null) => void;
	stop: () => void;
};

type FeedMode = 'opus-processor' | 'opus-worklet' | 'pcm';

async function pickFeedMode(track: MediaStreamTrack, contextRate: number): Promise<FeedMode> {
	const trackRate = track.getSettings().sampleRate;

	if (window.MediaStreamTrackProcessor && trackRate && (await isOpusSupported(trackRate))) {
		return 'opus-processor';
	}

	if (await isOpusSupported(contextRate)) return 'opus-worklet';

	return 'pcm';
}

const WORKLET_MODE: Record<FeedMode, CaptureMode> = {
	'opus-processor': 'level',
	'opus-worklet': 'float',
	pcm: 'pcm',
};

/**
 * Turns a microphone stream into frames for the AssemblyAI socket. Opus is
 * encoded with WebCodecs where it is available; otherwise an AudioWorklet
 * produces 16 kHz PCM. The AudioWorklet also reports the input level in
 * every mode, for the waveform.
 * @param stream The microphone stream to read frames from.
 * @param options Callbacks for the input level and capture failures.
 * @param options.onLevel Reports the current input level for the waveform.
 * @param options.onError Called when capture or encoding fails.
 */
export async function createAudioSource(
	stream: MediaStream,
	options: {
		onLevel: (level: number) => void;
		onError: (error: Error) => void;
	},
): Promise<AudioSource> {
	const track = stream.getAudioTracks()[0];

	if (!track) throw new Error('The microphone stream has no audio track');

	const context = new AudioContext();
	let sink: AudioFrameSink | null = null;
	let stopped = false;
	let encoder: OpusEncoder | null = null;
	let reader: ReadableStreamDefaultReader<AudioData> | null = null;

	try {
		await context.audioWorklet.addModule(captureWorkletUrl);
		if (context.state === 'suspended') await context.resume();

		const mode = await pickFeedMode(track, context.sampleRate);

		if (mode !== 'pcm') {
			encoder = createOpusEncoder({
				onPacket: (packet) => sink?.(packet),
				onError: options.onError,
			});
		}

		const input = context.createMediaStreamSource(stream);
		const node = new AudioWorkletNode(context, 'treams-capture', {
			numberOfInputs: 1,
			numberOfOutputs: 1,
			channelCount: 1,
			channelCountMode: 'explicit',
			processorOptions: { mode: WORKLET_MODE[mode] },
		});

		node.port.onmessage = (event: MessageEvent<CaptureMessage>) => {
			const message = event.data;

			if (message.type === 'level') options.onLevel(message.value);
			else if (message.type === 'pcm') sink?.(message.buffer);
			else if (message.type === 'float' && sink) {
				encoder?.encodeSamples(message.buffer, message.sampleRate);
			}
		};

		input.connect(node);
		node.connect(context.destination);

		if (mode === 'opus-processor' && window.MediaStreamTrackProcessor) {
			const processor = new window.MediaStreamTrackProcessor({ track });
			reader = processor.readable.getReader();

			void (async () => {
				try {
					while (!stopped && reader) {
						const { value, done } = await reader.read();

						if (done || !value) break;
						if (sink && encoder) encoder.encode(value);
						else value.close();
					}
				} catch (error) {
					if (!stopped) options.onError(error instanceof Error ? error : new Error(String(error)));
				}
			})();
		}

		return {
			encodingParams:
				mode === 'pcm' ? { encoding: 'pcm_s16le', sample_rate: '16000' } : { encoding: 'opus' },
			setSink: (next) => {
				sink = next;
			},
			stop: () => {
				stopped = true;
				sink = null;
				reader?.cancel().catch(() => {});
				node.port.onmessage = null;
				input.disconnect();
				node.disconnect();
				encoder?.close();
				context.close().catch(() => {});
			},
		};
	} catch (error) {
		stopped = true;
		encoder?.close();
		context.close().catch(() => {});
		throw error;
	}
}
